/**
 * Document Storage Configuration
 * Mirrors docstore.yaml (FMM_Document_Storage_Architecture_GDPR)
 *
 * This configuration defines the folder structure and document types
 * for GDPR-compliant document storage.
 */

import i18n from '../i18n/index.js';

// Scope definitions with icons and colors
export const SCOPE_CONFIG = {
  VEHICLE: {
    key: 'VEHICLE',
    label: 'Véhicules',
    labelSingular: 'Véhicule',
    icon: 'Car',
    color: 'blue',
    bgColor: 'bg-blue-50',
    hoverColor: 'hover:bg-blue-100',
    badgeColor: 'bg-blue-200 text-blue-800',
    textColor: 'text-blue-600',
    borderColor: 'border-blue-200'
  },
  DRIVER: {
    key: 'DRIVER',
    label: 'Conducteurs',
    labelSingular: 'Conducteur',
    icon: 'Users',
    color: 'green',
    bgColor: 'bg-green-50',
    hoverColor: 'hover:bg-green-100',
    badgeColor: 'bg-green-200 text-green-800',
    textColor: 'text-green-600',
    borderColor: 'border-green-200'
  },
  CLAIM: {
    key: 'CLAIM',
    label: 'Sinistres',
    labelSingular: 'Sinistre',
    icon: 'AlertTriangle',
    color: 'orange',
    bgColor: 'bg-orange-50',
    hoverColor: 'hover:bg-orange-100',
    badgeColor: 'bg-orange-200 text-orange-800',
    textColor: 'text-orange-600',
    borderColor: 'border-orange-200'
  },
  CONTRACT: {
    key: 'CONTRACT',
    label: 'Contrats',
    labelSingular: 'Contrat',
    icon: 'Briefcase',
    color: 'purple',
    bgColor: 'bg-purple-50',
    hoverColor: 'hover:bg-purple-100',
    badgeColor: 'bg-purple-200 text-purple-800',
    textColor: 'text-purple-600',
    borderColor: 'border-purple-200'
  },
  ADMIN: {
    key: 'ADMIN',
    label: 'Administration',
    labelSingular: 'Admin',
    icon: 'Shield',
    color: 'gray',
    bgColor: 'bg-gray-50',
    hoverColor: 'hover:bg-gray-100',
    badgeColor: 'bg-gray-200 text-gray-800',
    textColor: 'text-gray-600',
    borderColor: 'border-gray-200'
  }
};

/**
 * Folder structure per scope (from docstore.yaml entity_folder_standards)
 * Each folder has:
 * - code: Folder name on disk (e.g., "10_REGISTRATION")
 * - label: French display name
 * - description: What documents belong here
 * - documentTypes: Array of document_type values that map to this folder
 * - order: Display order
 * - color: Badge color for UI
 */
export const FOLDER_STRUCTURE = {
  VEHICLE: [
    {
      code: '00_META',
      label: 'Métadonnées',
      description: 'Informations techniques du dossier',
      documentTypes: ['VEHICLE_META'],
      order: 0,
      color: 'bg-slate-100 text-slate-700',
      hidden: true // System folder, not shown in UI
    },
    {
      code: '10_REGISTRATION',
      label: 'Immatriculation',
      description: 'Carte grise, certificat d\'immatriculation',
      documentTypes: ['VEHICLE_REGISTRATION'],
      order: 10,
      color: 'bg-blue-100 text-blue-800'
    },
    {
      code: '20_TECHNICAL_INSPECTION',
      label: 'Contrôle Technique',
      description: 'PV de contrôle technique, contre-visite',
      documentTypes: ['TECHNICAL_INSPECTION', 'TECHNICAL_INSPECTION_REPORT'],
      order: 20,
      color: 'bg-purple-100 text-purple-800'
    },
    {
      code: '30_INSURANCE',
      label: 'Assurance',
      description: 'Carte verte, attestation d\'assurance, contrat',
      documentTypes: ['VEHICLE_INSURANCE_CARD', 'VEHICLE_INSURANCE_CERTIFICATE', 'VEHICLE_INSURANCE_CONTRACT'],
      order: 30,
      color: 'bg-green-100 text-green-800'
    },
    {
      code: '40_LEASING_RENTAL',
      label: 'Location / Leasing',
      description: 'Contrat de location, LOA, LLD, facture d\'achat',
      documentTypes: ['LEASE_CONTRACT', 'PURCHASE_INVOICE', 'RENTAL_CONTRACT', 'FINANCING_CONTRACT'],
      order: 40,
      color: 'bg-indigo-100 text-indigo-800'
    },
    {
      code: '50_MAINTENANCE_INVOICES',
      label: 'Entretien & Factures',
      description: 'Factures d\'entretien, reparations, pieces',
      documentTypes: ['MAINTENANCE_INVOICE', 'REPAIR_INVOICE', 'SERVICE_INVOICE'],
      order: 50,
      color: 'bg-orange-100 text-orange-800'
    },
    {
      code: '60_CLAIMS',
      label: 'Sinistres Véhicule',
      description: 'Documents de sinistres liés au véhicule',
      documentTypes: ['VEHICLE_CLAIM_REPORT', 'VEHICLE_DAMAGE_PHOTO'],
      order: 60,
      color: 'bg-red-100 text-red-800'
    },
    {
      code: '90_MISC',
      label: 'Divers',
      description: 'Photos, documents divers',
      documentTypes: ['VEHICLE_PHOTO', 'VEHICLE_MISC'],
      order: 90,
      color: 'bg-gray-100 text-gray-700'
    }
  ],

  DRIVER: [
    {
      code: '00_META',
      label: 'Métadonnées',
      description: 'Informations techniques du dossier',
      documentTypes: ['DRIVER_META'],
      order: 0,
      color: 'bg-slate-100 text-slate-700',
      hidden: true
    },
    {
      code: '10_DRIVING_LICENCE',
      label: 'Permis de Conduire',
      description: 'Permis de conduire, categories, points',
      documentTypes: ['DRIVING_LICENCE', 'DRIVING_LICENCE_SCAN', 'POINTS_STATEMENT'],
      order: 10,
      color: 'bg-blue-100 text-blue-800'
    },
    {
      code: '20_ID_DOCUMENTS',
      label: 'Pieces d\'Identite',
      description: 'CNI, passeport, titre de sejour',
      documentTypes: ['IDENTITY_DOCUMENT', 'PASSPORT', 'RESIDENCE_PERMIT'],
      order: 20,
      color: 'bg-slate-100 text-slate-800'
    },
    {
      code: '30_CERTIFICATIONS',
      label: 'Certifications & Formations',
      description: 'FIMO, FCO, ADR, certificats medicaux',
      documentTypes: ['DRIVER_CERTIFICATION', 'MEDICAL_CERTIFICATE', 'FIMO_CERTIFICATE', 'FCO_CERTIFICATE', 'ADR_CERTIFICATE'],
      order: 30,
      color: 'bg-green-100 text-green-800'
    },
    {
      code: '40_SANCTIONS',
      label: 'Sanctions & Infractions',
      description: 'PV, amendes, suspensions',
      documentTypes: ['DRIVER_SANCTION', 'TRAFFIC_FINE', 'LICENCE_SUSPENSION'],
      order: 40,
      color: 'bg-red-100 text-red-800'
    }
  ],

  CLAIM: [
    {
      code: '10_DECLARATION',
      label: 'Declaration',
      description: 'Constat amiable, declaration, temoignages',
      documentTypes: ['ACCIDENT_DECLARATION', 'CLAIM_PHOTOS', 'DRIVER_STATEMENT', 'WITNESS_STATEMENT'],
      order: 10,
      color: 'bg-orange-100 text-orange-800'
    },
    {
      code: '20_EXPERT_REPORTS',
      label: 'Rapports d\'Expertise',
      description: 'Rapport expert, evaluation des dommages',
      documentTypes: ['EXPERT_REPORT', 'DAMAGE_ASSESSMENT', 'EXPERT_PHOTOS'],
      order: 20,
      color: 'bg-purple-100 text-purple-800'
    },
    {
      code: '30_INVOICES',
      label: 'Factures & Devis',
      description: 'Devis reparation, factures',
      documentTypes: ['REPAIR_INVOICE', 'REPAIR_QUOTE', 'PARTS_INVOICE'],
      order: 30,
      color: 'bg-emerald-100 text-emerald-800'
    },
    {
      code: '40_CORRESPONDENCE',
      label: 'Correspondance',
      description: 'Echanges avec assureur, tiers',
      documentTypes: ['CLAIM_CORRESPONDENCE', 'INSURER_LETTER', 'LAWYER_LETTER'],
      order: 40,
      color: 'bg-gray-100 text-gray-700'
    },
    {
      code: '50_SETTLEMENTS',
      label: 'Reglements',
      description: 'Indemnisation, quittance, cloture',
      documentTypes: ['SETTLEMENT_DOCUMENT', 'INDEMNITY_RECEIPT', 'CLAIM_CLOSURE'],
      order: 50,
      color: 'bg-green-100 text-green-800'
    }
  ],

  CONTRACT: [
    {
      code: '10_SPECIAL_CONDITIONS',
      label: 'Conditions Particulieres',
      description: 'CP, police, garanties specifiques, etat de parc',
      documentTypes: ['SPECIAL_CONDITIONS', 'CONTRACT_SCHEDULE', 'INSURANCE_POLICY', 'FLEET_SCHEDULE'],
      order: 10,
      color: 'bg-blue-100 text-blue-800'
    },
    {
      code: '20_GENERAL_CONDITIONS',
      label: 'Conditions Generales',
      description: 'CG, dispositions legales',
      documentTypes: ['GENERAL_CONDITIONS', 'LEGAL_TERMS'],
      order: 20,
      color: 'bg-gray-100 text-gray-700'
    },
    {
      code: '30_AMENDMENTS',
      label: 'Avenants',
      description: 'Modifications de contrat, avenants',
      documentTypes: ['CONTRACT_AMENDMENT', 'ADDENDUM', 'INSURANCE_AMENDMENT'],
      order: 30,
      color: 'bg-amber-100 text-amber-800'
    },
    {
      code: '40_INVOICES_RECEIPTS',
      label: 'Factures & Quittances',
      description: 'Factures, quittances, appels de prime',
      documentTypes: ['CONTRACT_INVOICE', 'RECEIPT', 'PREMIUM_NOTICE'],
      order: 40,
      color: 'bg-purple-100 text-purple-800'
    },
    {
      code: '50_ATTESTATIONS',
      label: 'Attestations & Certificats',
      description: 'Attestations assurance, certificats, mémos',
      documentTypes: ['INSURANCE_CERTIFICATE', 'INSURANCE_MEMO', 'INSURANCE_ATTESTATION', 'INSURANCE_MISC'],
      order: 50,
      color: 'bg-green-100 text-green-800'
    }
  ],

  ADMIN: [
    {
      code: 'ADMIN',
      label: 'Administration Générale',
      description: 'Documents administratifs generaux',
      documentTypes: ['TOOL_INVOICE', 'TOOL_MANUAL', 'GENERAL_INVOICE', 'GENERAL_QUOTE', 'ADMIN_MISC'],
      order: 10,
      color: 'bg-gray-100 text-gray-700'
    }
  ]
};

/**
 * Map document_type to folder code
 * @param {string} scope - VEHICLE, DRIVER, CLAIM, CONTRACT, ADMIN
 * @param {string} documentType - The document_type value
 * @returns {string} The folder code (e.g., "10_REGISTRATION")
 */
export function getFolder(scope, documentType) {
  const folders = FOLDER_STRUCTURE[scope] || [];

  // First, check if documentType matches any folder's documentTypes array
  for (const folder of folders) {
    if (folder.documentTypes.includes(documentType)) {
      return folder.code;
    }
  }

  // Second, check if documentType IS a folder code (backwards compatibility)
  const folderByCode = folders.find(f => f.code === documentType);
  if (folderByCode) {
    return folderByCode.code;
  }

  // Third, check if documentType starts with a folder code prefix (e.g., "10_REGISTRATION" matches "10_")
  for (const folder of folders) {
    const prefix = folder.code.split('_')[0];
    if (documentType.startsWith(prefix + '_')) {
      return folder.code;
    }
  }

  // Default to MISC or last folder
  const lastFolder = folders[folders.length - 1];
  return lastFolder?.code || '90_MISC';
}

/**
 * Get folder config by code
 * @param {string} scope - VEHICLE, DRIVER, CLAIM, CONTRACT, ADMIN
 * @param {string} folderCode - The folder code (e.g., "10_REGISTRATION")
 * @returns {Object|null} The folder configuration
 */
export function getFolderConfig(scope, folderCode) {
  const folders = FOLDER_STRUCTURE[scope] || [];
  return folders.find(f => f.code === folderCode) || null;
}

/**
 * Get all visible folders for a scope (excludes hidden system folders)
 * @param {string} scope - VEHICLE, DRIVER, CLAIM, CONTRACT, ADMIN
 * @returns {Array} Array of folder configurations
 */
export function getVisibleFolders(scope) {
  const folders = FOLDER_STRUCTURE[scope] || [];
  return folders.filter(f => !f.hidden).sort((a, b) => a.order - b.order);
}

/**
 * Document type labels (for display when type doesn't match a folder)
 */
export const DOCUMENT_TYPE_LABELS = {
  // Vehicle
  VEHICLE_REGISTRATION: 'Carte grise',
  VEHICLE_INSURANCE_CARD: 'Carte verte',
  VEHICLE_INSURANCE_CERTIFICATE: 'Attestation assurance',
  TECHNICAL_INSPECTION: 'Contrôle technique',
  LEASE_CONTRACT: 'Contrat location',
  PURCHASE_INVOICE: 'Facture achat',
  MAINTENANCE_INVOICE: 'Facture entretien',
  VEHICLE_PHOTO: 'Photo véhicule',
  VEHICLE_MISC: 'Document divers',
  // Driver
  DRIVING_LICENCE: 'Permis de conduire',
  IDENTITY_DOCUMENT: 'Piece d\'identite',
  DRIVER_CERTIFICATION: 'Certification',
  MEDICAL_CERTIFICATE: 'Certificat medical',
  DRIVER_SANCTION: 'Sanction',
  DRIVER_PHOTO: 'Photo conducteur',
  // Claim
  ACCIDENT_DECLARATION: 'Constat amiable',
  CLAIM_PHOTOS: 'Photos sinistre',
  DRIVER_STATEMENT: 'Declaration conducteur',
  EXPERT_REPORT: 'Rapport expert',
  REPAIR_INVOICE: 'Facture reparation',
  REPAIR_QUOTE: 'Devis reparation',
  CLAIM_CORRESPONDENCE: 'Correspondance',
  SETTLEMENT_DOCUMENT: 'Reglement',
  // Contract
  FLEET_SCHEDULE: 'État de parc',
  SPECIAL_CONDITIONS: 'Conditions particulières',
  INSURANCE_POLICY: 'Police d\'assurance',
  CONTRACT_SCHEDULE: 'Tableau des garanties',
  GENERAL_CONDITIONS: 'Conditions générales',
  LEGAL_TERMS: 'Mentions légales',
  CONTRACT_AMENDMENT: 'Avenant',
  ADDENDUM: 'Additif',
  INSURANCE_AMENDMENT: 'Avenant assurance',
  CONTRACT_INVOICE: 'Facture / Quittance',
  RECEIPT: 'Reçu',
  PREMIUM_NOTICE: 'Avis d\'échéance',
  INSURANCE_CERTIFICATE: 'Certificat d\'assurance',
  INSURANCE_MEMO: 'Mémo assurance',
  INSURANCE_ATTESTATION: 'Attestation d\'assurance',
  INSURANCE_MISC: 'Document divers assurance',
  // Admin
  TOOL_INVOICE: 'Facture outillage',
  TOOL_MANUAL: 'Manuel',
  GENERAL_INVOICE: 'Facture generale',
  GENERAL_QUOTE: 'Devis'
};

/**
 * Get label for a document type
 * @param {string} documentType - The document_type value
 * @returns {string} The French label
 */
export function getDocumentTypeLabel(documentType) {
  // La traduction est résolue à l'appel (jamais mémorisée au chargement du module),
  // DOCUMENT_TYPE_LABELS restant le libellé FR de référence et le repli.
  if (DOCUMENT_TYPE_LABELS[documentType]) {
    return i18n.t(`documents.types.${documentType}`, { defaultValue: DOCUMENT_TYPE_LABELS[documentType] });
  }

  // Try to match as folder code by extracting meaningful part
  // e.g., "20_TECHNICAL_INSPECTION" -> "TECHNICAL_INSPECTION"
  if (documentType && documentType.includes('_')) {
    const parts = documentType.split('_');
    if (parts[0].match(/^\d+$/)) {
      // First part is numeric (folder prefix), try the rest
      const remainder = parts.slice(1).join('_');
      if (DOCUMENT_TYPE_LABELS[remainder]) {
        return i18n.t(`documents.types.${remainder}`, { defaultValue: DOCUMENT_TYPE_LABELS[remainder] });
      }
    }
  }

  // Return as-is if no match found
  return documentType;
}

/**
 * Determine if a document should be archived (RGPD compliance)
 * Archive criteria:
 * - Document is expired (valid_to < today)
 * - Document has status 'archived' or 'inactive'
 * - Document is marked for deletion
 * - Document is an old version (superseded)
 *
 * @param {Object} doc - Document object
 * @returns {Object} { isArchived: boolean, reason: string }
 */
export function getArchiveStatus(doc) {
  // Check if marked for deletion
  if (doc.marked_for_deletion || doc.deleted_at) {
    return { isArchived: true, reason: 'Supprime' };
  }

  // Check status
  if (doc.status === 'archived' || doc.status === 'inactive') {
    return { isArchived: true, reason: 'Archive' };
  }

  // Check expiry
  if (doc.valid_to) {
    const today = new Date();
    const expiry = new Date(doc.valid_to);
    if (expiry < today) {
      return { isArchived: true, reason: 'Expire' };
    }
  }

  // Check version (if not the latest)
  if (doc.is_superseded) {
    return { isArchived: true, reason: 'Ancienne version' };
  }

  return { isArchived: false, reason: null };
}

/**
 * Group documents by archive status
 * @param {Array} documents - Array of document objects
 * @returns {Object} { active: [], archived: [] }
 */
export function groupByArchiveStatus(documents) {
  const active = [];
  const archived = [];

  documents.forEach(doc => {
    const { isArchived } = getArchiveStatus(doc);
    if (isArchived) {
      archived.push(doc);
    } else {
      active.push(doc);
    }
  });

  return { active, archived };
}

const docstoreConfig = {
  SCOPE_CONFIG,
  FOLDER_STRUCTURE,
  DOCUMENT_TYPE_LABELS,
  getFolder,
  getFolderConfig,
  getVisibleFolders,
  getDocumentTypeLabel,
  getArchiveStatus,
  groupByArchiveStatus
};

export default docstoreConfig;
